import React, { useCallback, useEffect } from 'react';
import webCore from '@happylife-a/web-core';
import { useTable, usePagination as useReactTablePagination } from 'react-table';
import { AiOutlineArrowDown, AiOutlineArrowUp } from 'react-icons/ai';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';
import {
  Table as ChakraTable,
  Thead,
  Tbody,
  Th,
  Tr,
  Td,
  Button,
  HStack,
  Stack,
  Icon,
  Text,
  Select,
  Flex,
  Box,
  Grid,
  GridItem,
  Spinner,
  VStack,
} from '@chakra-ui/react';
import FilterBy from '../../components/FilterBy';
import FilterByDate from '../../components/FilterByDate';
import { theme } from '../../theme';
import { FilterIcon } from '../../theme/icons';
import Search from './Search';

export { default as usePagination } from './usePagination';
export { default as useFiltering } from './useFiltering';
export { default as useFilterAndPagination } from './useFilterAndPagination';

const pageSizeArray = [10, 20, 30, 40, 50];

function Table({
  overflowX = 'auto',
  columns,
  data,
  limit,
  offset,
  sort,
  total,
  loading,
  searchPlaceholder,
  onPageSizeChange: onLimitChange,
  goToCurrentPage,
  onSort,
  onSearch,
  onNextPage,
  onPrevPage,
  initialDateRange,
  clearFilterComponent,
  onFilterByDate,
  paginationPageSizeChangable = true,
  showResult = true,
  isFilterVisible = false,
  isRowBackgroundTransparent = false,
  heading,
  onRowClick,
  emptyState,
}) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    // page,
    rows,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    // Get the state from the instance
  } = useTable(
    {
      columns: columns,
      data: data,
      initialState: { pageIndex: offset / limit, pageSize: limit },
      manualPagination: true,
      pageCount: Math.ceil(total / limit),
    },
    useReactTablePagination
  );

  const pageIndex = offset / limit;
  const pageSize = limit;

  const { t } = webCore.contexts.useTranslation();

  const onPageSizeChange = useCallback(
    (newPageSize) => {
      setPageSize(newPageSize);
      gotoPage(0);
      onLimitChange(newPageSize);
    },
    [setPageSize, gotoPage, onLimitChange]
  );

  const goNextPage = useCallback(() => {
    nextPage();
    onNextPage();
  }, [nextPage, onNextPage]);

  const goPrevPage = useCallback(() => {
    previousPage();
    onPrevPage();
  }, [previousPage, onPrevPage]);

  const search = useCallback(
    (text) => {
      gotoPage(0);
      onSearch(text);
    },
    [gotoPage, onSearch]
  );

  const onCurrentPage = useCallback(
    (value) => {
      const page = value ? Number(value) - 1 : 0;
      gotoPage(page);
      goToCurrentPage(page);
    },
    [gotoPage, goToCurrentPage]
  );

  useEffect(() => {
    gotoPage(offset / limit);
  }, [gotoPage, offset, limit]);

  const onSortClick = (id, sorting) => {
    if (sorting === false) {
      return;
    }
    onSort(id);
  };

  return (
    <>
      <Stack spacing={8} mt={4}>
        {(typeof onSearch === 'function' || isFilterVisible) && (
          <Flex gap={8}>
            {typeof onSearch === 'function' && (
              <Search placeholder={searchPlaceholder} onSearch={search} />
            )}
            {isFilterVisible && (
              <FilterBy
                variant="variant3"
                height="40px"
                icon={FilterIcon}
                text="Filter"
                clearComponent={clearFilterComponent}
              >
                <FilterByDate
                  initialDateRange={initialDateRange}
                  onSubmit={(date) => onFilterByDate(date)}
                  isLoading={false}
                />
              </FilterBy>
            )}
          </Flex>
        )}
        {heading && (
          <Text variant="sub-head-semi" fontSize="3xl">
            {heading}
          </Text>
        )}
        <Box
          maxW="100%"
          overflowX={overflowX}
          overflowY="hidden"
          boxShadow="0px 0px 8px -6px"
          borderRadius="10px"
        >
          <ChakraTable {...getTableProps()} border="unset">
            <Thead>
              {headerGroups.map((headerGroup, index) => (
                <Tr
                  key={`header-group-${index}`}
                  {...headerGroup.getHeaderGroupProps()}
                  backgroundColor={isRowBackgroundTransparent && 'blackAlpha.100'}
                >
                  {headerGroup.headers.map((column, groupIndex) => {
                    if (column.isHidden) {
                      column.toggleHidden(true);
                    }
                    return (
                      <Th
                        key={`header-column-${column.id}-${groupIndex}`}
                        {...column.getHeaderProps()}
                        bg="#f8fafc"
                      >
                        <HStack
                          spacing={1}
                          alignItems="center"
                          cursor={column.isSortable === true && 'pointer'}
                          onClick={() =>
                            onSortClick(column.sortFieldName || column.id, column.isSortable)
                          }
                        >
                          <Text
                            cursor={column.isSortable === true && 'pointer'}
                            variant="body-reg-lg"
                            color={isRowBackgroundTransparent && 'grey.900'}
                            fontWeight="600"
                            textTransform="uppercase"
                            fontSize="13px"
                            py={4}
                          >
                            {column.render('Header')}
                          </Text>
                          {column.isSortable === true && (
                            <Icon as={theme.icons.TableSortIcon} w={4} h={4} fill="none" />
                          )}

                          {sort?.field === column.id && (
                            <>
                              {sort.direction === 'asc' ? (
                                <Icon w={15} h={15} as={AiOutlineArrowUp} />
                              ) : (
                                <Icon w={15} h={15} as={AiOutlineArrowDown} />
                              )}
                            </>
                          )}
                        </HStack>
                      </Th>
                    );
                  })}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {rows?.map((row, i) => {
                prepareRow(row);
                return (
                  <Tr
                    cursor="pointer"
                    key={`row-${row.id}-${i}`}
                    {...row.getRowProps()}
                    _hover={{ background: 'gray.100' }}
                    transition="all 100ms ease-in-out"
                    onClick={(e) => {
                      if (typeof onRowClick === 'function') {
                        onRowClick({ event: e, row: row, index: i });
                      }
                    }}
                  >
                    {row.cells.map((cell, index) => (
                      <Td
                        key={`cell-${cell.value}-${index}`}
                        {...cell.getCellProps()}
                        backgroundColor={isRowBackgroundTransparent && 'transparent !important'}
                      >
                        {cell.render('Cell')}
                      </Td>
                    ))}
                  </Tr>
                );
              })}
              {showResult && loading && (
                <Tr>
                  <Td colSpan="10000">
                    <VStack>
                      <Spinner color="primary.500" />
                    </VStack>
                  </Td>
                </Tr>
              )}
              {showResult && !loading && data?.length === 0 && (
                <Tr>
                  <Td colSpan="10000" borderBottom="none">
                    <VStack spacing={3} py={12}>
                      {emptyState?.icon && (
                        <Flex
                          w="64px"
                          h="64px"
                          borderRadius="full"
                          bg="primary.50"
                          align="center"
                          justify="center"
                        >
                          <Icon
                            as={emptyState.icon}
                            w={8}
                            h={8}
                            fill="none"
                            stroke="currentColor"
                            color="primary.500"
                          />
                        </Flex>
                      )}
                      <Text fontWeight="600" fontSize="lg" color="grey.900">
                        {t(emptyState?.title || 'No data to show')}
                      </Text>
                      {emptyState?.description && (
                        <Text variant="body-reg-lg" color="grey.700">
                          {t(emptyState.description)}
                        </Text>
                      )}
                    </VStack>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </ChakraTable>
        </Box>
      </Stack>

      {total > 0 && limit > 0 && data?.length > 0 && (
        <Box pt={{ base: 4, md: 10 }} w="full" position="relative" mt={{ base: 20, md: 3 }}>
          <Box position="absolute" left="0" bottom="0" right="0" width="100%">
            <Grid
              templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }}
              gap={{ base: 3, md: 6 }}
              alignItems="center"
            >
              {/* Left controls */}
              <GridItem>
                <Flex
                  gap={{ base: 2, md: 3 }}
                  alignItems="center"
                  justifyContent={{ base: 'space-between', md: 'flex-start' }}
                  flexWrap="wrap"
                >
                  {paginationPageSizeChangable && (
                    <Flex align="center" gap={2}>
                      <Select
                        isDisabled={loading}
                        w={{ base: '71px', sm: '140px', md: '90px' }}
                        border="transparent"
                        bg="rgba(94, 99, 102, 0.08)"
                        h={{ base: 9, md: 6 }}
                        value={pageSize}
                        onChange={(e) => onPageSizeChange(Number(e.target.value))}
                      >
                        {pageSizeArray.map((pageSizeItem) => (
                          <option key={`PageSize-${pageSizeItem}`} value={pageSizeItem}>
                            {pageSizeItem}
                          </option>
                        ))}
                      </Select>
                      <Text variant="body-reg-lg" fontSize={{ base: 'sm', md: 'md' }}>
                        {t('Items per page')}
                      </Text>
                    </Flex>
                  )}

                  <Text variant="body-reg-lg" fontSize={{ base: 'sm', md: 'md' }}>
                    {loading ? (
                      <Text as="span">{t('Loading')}…</Text>
                    ) : (
                      <Text as="span">
                        {pageIndex === 0 && total > 0 ? 1 : pageIndex * pageSize + 1}-
                        {(pageIndex + 1) * pageSize > total ? total : (pageIndex + 1) * pageSize}{' '}
                        {t('of')} {total} {t('Pages')}
                      </Text>
                    )}
                  </Text>
                </Flex>
              </GridItem>

              {/* Right controls */}
              <GridItem textAlign={{ base: 'left', md: 'right' }}>
                <Flex
                  gap={{ base: 2, md: 3 }}
                  justifyContent={{ base: 'space-between', md: 'flex-end' }}
                  align="center"
                  flexWrap="wrap"
                >
                  <Flex align="center" gap={2}>
                    <Select
                      isDisabled={loading}
                      w={{ base: '71px', sm: '140px', md: '90px' }}
                      border="transparent"
                      bg="rgba(94, 99, 102, 0.08)"
                      h={{ base: 9, md: 6 }}
                      value={pageIndex + 1}
                      onChange={(e) => onCurrentPage(Number(e.target.value))}
                    >
                      {pageOptions?.map((pageOptionItem) => (
                        <option key={`PageOption-${pageOptionItem}`} value={pageOptionItem + 1}>
                          {pageOptionItem + 1}
                        </option>
                      ))}
                    </Select>

                    <Text variant="body-reg-lg" fontSize={{ base: 'sm', md: 'md' }}>
                      {t('Pages count', { pages: pageOptions?.length })}
                    </Text>
                  </Flex>

                  <Flex align="center" gap={{ base: 1, md: 2 }}>
                    <Button
                      onClick={goPrevPage}
                      variant={{ base: 'ghost', md: 'link' }}
                      size={{ base: 'md', md: 'sm' }}
                      borderRadius="md"
                      isDisabled={!canPreviousPage || loading}
                      aria-label={t('Previous page')}
                      minW={{ base: 10, md: 'auto' }}
                      h={{ base: 10, md: 8 }}
                    >
                      <FaAngleLeft fill="black" />
                    </Button>

                    <Button
                      onClick={goNextPage}
                      variant={{ base: 'ghost', md: 'link' }}
                      size={{ base: 'md', md: 'sm' }}
                      borderRadius="md"
                      isDisabled={!canNextPage || loading}
                      aria-label={t('Next page')}
                      minW={{ base: 10, md: 'auto' }}
                      h={{ base: 10, md: 8 }}
                    >
                      <FaAngleRight fill="black" />
                    </Button>
                  </Flex>
                </Flex>
              </GridItem>
            </Grid>
          </Box>
        </Box>
      )}
    </>
  );
}

export default Table;
